// 12 lessons — verbatim per spec §2
export const LESSONS = [
  {
    n: 1,
    slug: "authority-begins-before-first-word",
    title: "Authority Begins Before the First Word",
    teaser: "Why impact is created before you speak.",
    body: [
      "Authority is rarely produced by a single argument. It begins earlier: in your pace, your posture, your gaze, your inner state, and the way you enter a room. People read these signals automatically, without thinking about them consciously.",
      "That is why finding the right words is not enough. If your body sends insecurity, your content has to fight against it. If your state is clear, even simple sentences carry more weight. This lesson shifts the focus away from performance and toward presence. Not \"How do I sound convincing?\" but \"What does my whole system send before I even speak?\"",
    ],
    impulse: "Observe three people today before they speak. What is already communicated before the first sentence?",
  },
  {
    n: 2,
    slug: "scripts-lose-to-social-skill",
    title: "Scripts Lose to Social Skill",
    teaser: "Why techniques only work when the person behind them is stable.",
    body: [
      "Many people search for perfect phrasings, conversation openers, or tactics. The problem: a good script in insecure hands comes across as artificial. A mediocre script in the hands of a socially composed person can land powerfully.",
      "Influence is therefore not primarily a question of words, but of the skills that carry the words: listening, timing, calm, humor, presence, contact. Whoever only collects tactics builds a façade. Whoever trains social skill builds substance. AuthorityOS does not teach \"Say this sentence,\" but \"Become the person who can carry this sentence credibly.\"",
    ],
    impulse: "Recall someone who comes across as strong with simple words. What carries that impact?",
  },
  {
    n: 3,
    slug: "small-talk-is-training-ground",
    title: "Small Talk Is the Training Ground",
    teaser: "Small conversations aren't a distraction; they're basic training.",
    body: [
      "Small talk is often underrated because it seems superficial. In reality, it is the practice field for almost every social skill: making contact, lowering tension, showing interest, keeping a conversation flowing, spotting points of connection. Whoever avoids small talk avoids not only triviality but often the first gateway to connection.",
      "Authority is built not only in big moments but in many small social contacts, where your system learns: I can deal with people. I can stay present. I can open and carry a conversation. That is exactly why small talk is not a side topic but a foundation.",
    ],
    impulse: "Start a short conversation today with no goal. Don't impress; just make contact.",
  },
  {
    n: 4,
    slug: "interest-makes-you-interesting",
    title: "Interest Makes You Interesting",
    teaser: "Why genuine attention creates more authority than self-presentation.",
    body: [
      "People are rarely drawn to the person who talks most about themselves. They are drawn to the person around whom they feel seen. Genuine attention is therefore an authority lever.",
      "Whoever listens, responds, asks, and visibly goes along creates in the other person the feeling: my words land. That builds trust and opens depth. Authority here is not dominance but leadership through attention. The strongest person in a conversation is not always the loudest. Often it is the person who holds the room so that others open up.",
    ],
    impulse: "Have a conversation where you don't try to seem interesting. Just try to be genuinely interested.",
  },
  {
    n: 5,
    slug: "moment-people-hand-over-responsibility",
    title: "The Moment People Hand Over Responsibility",
    teaser: "How authority can trigger obedience, orientation, and blind following.",
    body: [
      "Authority has a dark side and a useful side. Under authority, people can shift into a state where they question less and follow more. They orient toward titles, clothing, confidence, group behavior, or the feeling that someone is \"in charge.\" This mechanism is powerful, and therefore dangerous when used unconsciously.",
      "For AuthorityOS this matters: authority should not serve to make people smaller. It should give orientation without destroying personal responsibility. Real authority makes others clearer, not more dependent.",
    ],
    impulse: "Where do you follow authority without checking for yourself? Titles, status, presence, or group pressure?",
  },
  {
    n: 6,
    slug: "invisible-authority-tripwires",
    title: "The Invisible Authority Tripwires",
    teaser: "The signals people unconsciously read as authority.",
    body: [
      "People don't recognize authority through content alone. They react to so-called tripwires: movement, appearance, self-assurance, attention, and an inner sense of coherence.",
      "Slow movements read as more controlled than hurried ones. A well-groomed appearance is taken seriously faster. Calm attention creates trust. Congruence feels safe; incongruence feels off, even when no one can say exactly why. These signals can be imitated briefly but not faked permanently. So the central question remains: which habits would produce these signals automatically?",
    ],
    impulse: "Record yourself on video once. Don't judge, just observe: pace, posture, gaze, hands, voice.",
  },
  {
    n: 7,
    slug: "remove-what-destroys-authority",
    title: "Remove What Destroys Authority",
    teaser: "Before you build authority, you have to cut the saboteurs.",
    body: [
      "Many want to come across as stronger without removing the signals that undermine their impact. Restlessness, over-apologizing, negativity, poor self-care, bragging, aggression, defensive silence, lack of self-control, or constant complaining all send the same message: this system doesn't lead itself.",
      "The fastest progress often comes not from a new behavior but from cutting an old pattern. AuthorityOS therefore thinks diagnostically: what do you need to stop, not add? What is costing you authority even though you've long since normalized it?",
    ],
    impulse: "Choose one sabotaging signal to consciously reduce this week.",
  },
  {
    n: 8,
    slug: "self-assurance-is-built-on-proof",
    title: "Self-Assurance Is Built on Proof",
    teaser: "Why you can't talk yourself into self-assurance.",
    body: [
      "Self-assurance doesn't grow through affirmations but through proof. You trust yourself when you repeatedly experience yourself acting even when it's uncomfortable. A stranger approached. A boundary set. A compliment accepted. Criticism not taken personally. A decision made.",
      "With each small proof, your system learns: I can carry this. That is why self-assurance is not an idea but a collection of stored experiences. Whoever wants to build it needs not more self-talk but more honest repetition.",
    ],
    impulse: "Take one small action today that gives you a piece of proof.",
  },
  {
    n: 9,
    slug: "discipline-is-care-for-future-self",
    title: "Discipline Is Care for Your Future Self",
    teaser: "Why order and routine are not harshness but self-respect.",
    body: [
      "Discipline is often misunderstood as strictness. In the authority context, it is closer to care. You tidy up today so your morning starts clearer. You plan your week so your future self doesn't have to improvise. You keep commitments so your self-image stays stable.",
      "Discipline means: I don't keep letting my future self pay the bill for my present avoidance. Whoever thinks this way needs less willpower, because the environment does the work. Authority emerges when your life shows: this person can lead themselves.",
    ],
    impulse: "Do one thing today that your future self would thank you for tomorrow.",
  },
  {
    n: 10,
    slug: "composure-middle-between-collapse-posturing",
    title: "Composure Is the Middle Between Collapse and Posturing",
    teaser: "Why real authority neither shrinks nor has to inflate itself.",
    body: [
      "Under pressure, people often show one of two failure modes. Collapse: they shrink, back away, lose their voice, gaze, and ability to act. Posturing: they compensate for insecurity with hardness, volume, or dominance. Neither is real authority.",
      "Composure lies in between. It is calm presence under tension. You stay approachable but not steerable. You stay clear but not aggressive. Precisely this middle is magnetic, because it sends security without a show. AuthorityOS treats composure as the core state: not control over others, but stability within you.",
    ],
    impulse: "Recall a high-pressure situation. Did you tend toward Collapse or Posturing?",
  },
  {
    n: 11,
    slug: "connection-mode-instead-of-performance-mode",
    title: "Connection Mode Instead of Performance Mode",
    teaser: "How to get out of self-monitoring and back into real contact.",
    body: [
      "In Performance Mode you are inwardly busy with yourself: How am I coming across? Was that weird? What do they think? What do I say next? As a result, you lose contact even though you're physically present.",
      "Connection Mode reverses the focus. You observe not yourself but the other person. You listen, ask, respond, allow pauses. Paradoxically, this makes you come across as more composed, because you're no longer circling your own impact. Social authority is not created when you perform perfectly. It's created when people feel: you are truly here.",
    ],
    impulse: "Have a conversation where you return every time you catch yourself self-monitoring.",
  },
  {
    n: 12,
    slug: "cause-before-symptom",
    title: "Cause Before Symptom",
    teaser: "The central lesson — work on the lever, not the visible problem.",
    body: [
      "Most people work on symptoms. They want to seem more confident, appear more disciplined, come across as more charismatic, or earn more respect. But these things are results. AuthorityOS reverses the direction: which habits produce these traits? Which traits produce this impact? Which bottleneck is the system stuck on right now?",
      "Whoever hits the cause needs less pressure. Whoever works only on the symptom has to keep patching. This final lesson sums up the whole course: authority is not a trick. It is the visible consequence of a well-led life.",
    ],
    impulse: "Frame your current bottleneck not as a feeling but as a habit that is missing or needs to be cut.",
  },
];
